import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  FileText,
  FolderOpen,
  Share2,
  ScrollText,
  Wrench,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import {
  PRODUCT_NAME_MARKETING,
  ROUTE_PRODUCT_PROFESSIONAL,
  TARIF_PROFESSIONAL,
} from '../../../constants';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow, type T05ReferoAccent } from './T05ReferoFeatureRow';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const DOKUMENTE_LEAD = { interest: 'Orbis Dokumente – Demo', quelle: 't05-product-dokumente' };

interface Props extends T05ProductPageProps {}

const FEATURES = [
  {
    id: 'onlyoffice',
    icon: FileText,
    accent: 'coral' as T05ReferoAccent,
    label: 'OnlyOffice',
    title: 'OnlyOffice im Browser',
    desc: 'Word-naher Editor direkt im Portal: Schriftsätze, Verträge und Aktenvermerke bearbeiten — Speicherung auf Ihrer Nextcloud, kein Export zu US-Cloud.',
    panelTitle: 'onlyoffice://akte/schriftsatz.docx',
    rows: [
      { label: 'Schriftsatz_Einrede.docx', badge: 'Bearbeiten' },
      { label: 'Letzte Änderung: heute', badge: 'Version' },
      { label: 'Speichern → Nextcloud', badge: 'Edge' },
    ],
  },
  {
    id: 'ordner',
    icon: FolderOpen,
    accent: 'voltage' as T05ReferoAccent,
    label: 'Aktenstruktur',
    title: 'Mandantenordner & Akte',
    desc: 'Klare Ordnerstruktur pro Mandat und Fall — PocketBase-Metadaten und Nextcloud-Ablage in einer logischen Akte, ohne Tool-Chaos.',
    panelTitle: 'orbis://mandant/weber_2026',
    rows: [
      { label: 'Mandantenmappe_2026/', badge: 'Ordner' },
      { label: 'Korrespondenz/', badge: '12 Dateien' },
      { label: 'Schriftsätze/', badge: '4 Dateien' },
    ],
  },
  {
    id: 'freigabe',
    icon: Share2,
    accent: 'gold' as T05ReferoAccent,
    label: 'Nextcloud-Freigabe',
    title: 'Nextcloud-Freigabe',
    desc: 'Sichere Freigabe mit Ablaufdatum und Passwort — Mandanten erhalten nur die freigegebenen Dokumente, alles auf Ihrem Server in der EU.',
    panelTitle: 'nc://share/mandant-link',
    rows: [
      { label: 'Freigabe: Unterlagen Weber', badge: 'Aktiv' },
      { label: 'Läuft ab: 14 Tage', badge: 'NC' },
      { label: 'Download-Protokoll', badge: 'Audit' },
    ],
  },
  {
    id: 'stirling',
    icon: Wrench,
    accent: 'trust' as T05ReferoAccent,
    label: 'PDF-Werkzeuge',
    title: 'Stirling PDF',
    desc: 'Leichter PDF-Werkzeugkasten zusätzlich zu OnlyOffice: Zusammenführen, Teilen, Komprimieren — serverseitig auf Ihrem Edge, kein Drittanbieter-Upload.',
    panelTitle: 'pdf://werkzeuge',
    rows: [
      { label: 'PDF zusammenführen', badge: 'Merge' },
      { label: 'Seiten extrahieren', badge: 'Split' },
      { label: 'Komprimieren', badge: 'Opt.' },
    ],
  },
  {
    id: 'protokolle',
    icon: ScrollText,
    accent: 'coral' as T05ReferoAccent,
    label: 'Protokoll-Archiv',
    title: 'Protokoll-Archiv',
    desc: 'Revisionssichere Ablage von Sitzungs- und Mandantenprotokollen — durchsuchbar, mandatsbezogen, DSGVO-konform auf Ihrem Server.',
    panelTitle: 'orbis://protokolle',
    rows: [
      { label: 'Sitzungsprotokoll_03-2026.pdf', badge: 'Archiv' },
      { label: 'Mandant Weber · Akte 24/26', badge: 'Zuordnung' },
      { label: 'Unveränderbar gespeichert', badge: 'NC' },
    ],
  },
] as const;

const ABLAUF_STEPS = [
  {
    num: 1,
    icon: FileText,
    text: 'Schriftsatz in OnlyOffice bearbeiten und direkt in der Mandantenmappe speichern',
  },
  {
    num: 2,
    icon: Wrench,
    text: 'PDF-Werkzeuge mit Stirling PDF — merge, split, compress auf Ihrem Server',
  },
  {
    num: 3,
    icon: Share2,
    text: 'Mandantenfreigabe per Nextcloud-Link mit Ablauf und Protokoll',
  },
  {
    num: 4,
    icon: ScrollText,
    text: 'Protokolle revisionssicher archivieren — mandatsbezogen und durchsuchbar',
  },
] as const;

function ProductPanel({
  title,
  rows,
}: {
  title: string;
  rows: readonly { label: string; badge: string }[];
}) {
  return (
    <div className="t05-product-panel t05-product-panel--stack" aria-label={title}>
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">{title}</p>
      <div className="t05-product-panel__grid">
        {rows.map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export const ProductDokumente: React.FC<Props> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, DOKUMENTE_LEAD);
  const openDemo = () => lead.openDemo(DOKUMENTE_LEAD.interest);

  return (
    <T05ProductShell
      productLabel="Dokumente & Akten"
      navAnchors={[
        { href: '#bausteine', label: 'Bausteine' },
        { href: '#ablauf', label: 'Ablauf' },
        { href: '#cta', label: 'Demo' },
      ]}
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">Orbis · Self-Hosted · EU</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          Dokumente & Akten.
          <span className="t05-headline__accent t05-accent--dokumente"> Eine Akte.</span>
        </h1>
        <p className="t05-lead reveal-up">
          <strong>{PRODUCT_NAME_MARKETING}</strong> vereint OnlyOffice, Nextcloud und Protokolle in einer mandatsbezogenen
          Akte — auf Ihrem Edge-Server, ohne US-Cloud-Export.
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <section className="t05-container" id="bausteine" aria-label="Dokumente Bausteine">
        <div className="t05-section__header reveal-up">
          <p className="t05-kicker">Bausteine</p>
          <h2 className="t05-title">Alles in einer Akte.</h2>
          <p className="t05-subtitle">
            Bearbeiten, ablegen, freigeben und archivieren — ohne zwischen Fremdtools zu wechseln.
          </p>
        </div>

        <div className="t05-refero-features">
          {FEATURES.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <T05ReferoFeatureRow
                key={feature.id}
                reverse={index % 2 === 1}
                accent={feature.accent}
                label={feature.label}
                labelIcon={Icon}
                title={feature.title}
                description={feature.desc}
                bullets={feature.rows.map((row) => ({
                  icon: Check,
                  text: row.label,
                }))}
                visual={<ProductPanel title={feature.panelTitle} rows={feature.rows} />}
              />
            );
          })}
        </div>
      </section>

      <section
        className="t05-prod-section t05-prod-section--tight t05-prod-ablauf t05-container"
        id="ablauf"
        aria-labelledby="dokumente-ablauf-heading"
      >
        <div className="t05-section__header reveal-up">
          <p className="t05-kicker">Ablauf</p>
          <h2 id="dokumente-ablauf-heading" className="t05-title">
            Vom Entwurf zum Protokoll.
          </h2>
          <p className="t05-subtitle">
            Ein durchgängiger Workflow in Ihrer Akte — bearbeiten, freigeben und archivieren ohne Tool-Wechsel.
          </p>
        </div>

        <div className="t05-prod-ablauf__band reveal-up">
          <ol className="t05-prod-ablauf__steps stagger-children">
            {ABLAUF_STEPS.map((step) => {
              const Icon = step.icon;
              return (
                <li key={step.num} className="t05-prod-ablauf__step">
                  <div className="t05-prod-ablauf__step-head">
                    <span className="t05-prod-ablauf__num" aria-hidden>
                      {step.num}
                    </span>
                    <span className="t05-prod-ablauf__icon" aria-hidden>
                      <Icon size={18} />
                    </span>
                  </div>
                  <p className="t05-prod-ablauf__text">{step.text}</p>
                </li>
              );
            })}
          </ol>
          <p className="t05-prod-ablauf__footnote">
            Enthalten in {TARIF_PROFESSIONAL} und Orbis Premium · Self-Hosted auf Ihrem EU-Server
          </p>
        </div>
      </section>

      <section className="t05-final-cta t05-container" id="cta">
        <div className="t05-final-cta__band reveal-up">
          <div className="t05-final-cta__inner">
            <h2 className="t05-title">Dokumente ohne Tool-Chaos?</h2>
            <p className="t05-subtitle">
              Persönliche Demo — wir zeigen Akte, OnlyOffice und Freigabe auf Ihrem Edge-Stack.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                Demo anfragen <ArrowRight size={18} aria-hidden />
              </button>
              <Link to={ROUTE_PRODUCT_PROFESSIONAL} className="t05-btn t05-btn--ghost">
                {TARIF_PROFESSIONAL} ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};
